import { EAnimalUrls } from 'data/urls/animal.url';
import { COMMON_ALLOWED_ANIMAL_ACTIONS_URL } from 'data/urls/common.url';
import {
  TAllowedAnimalActionsDB,
  TAnimalApplicationTargetUI,
  TAllowedAnimalActionsUI,
} from 'data/types/AnimalActions.types';
import { allowedAnimalActionsConverter } from 'data/convertors/AnimalActions.converters';
import {
  TPageableDataWithContentUi,
  TTableFilters,
} from 'data/types/Table.types';
import {
  ESortFields,
  TAnimalDB,
  TAnimalDetail,
  TAnimalDetailUi,
  TAnimalUI,
  TVetRecordExpirationUpdateUI,
  TAnimalWarningVerificationDB,
  TAnimalWarningVerificationUI,
  TUpdateAnimalTypeUI,
  TApproveAnimalTypeConvUI,
  TRejectAnimalTypeConvUI,
  TChargingStatusUpdateUI,
  TChargingStatusUpdateDB,
  TMarkAsPNCUI,
  TMarkAsPNCDB,
  TMarkKnownUI,
  TMarkKnownDB,
  TRemoveDiscoveredUI,
  TRemoveDiscoveredDB,
  TMarkAsDiscoverdDB,
  TMarkAsDiscoverdUI,
  TAnimalDetailVetRecordCreateUi,
  TUpdateAnimalNameUI,
  TUpdateAnimalSpeciesUI,
  TUpdateAnimalBreedUI,
  TUpdateAnimalMicrochipDateUI,
  TUpdateAnimalBirthDateUI,
  TUpdateAnimalWeightUI,
  TUpdateAnimalHouseTrainedUI,
  TUpdateAnimalGenderUI,
  TUpdateAnimalTrainedForUI,
  TUpdateAnimalReproductiveUI,
  TUpdateAnimalProfilePictureUI,
  TUpdateAnimalColorsUI,
  TUpdateAnimalAggressiveBehaviorUI,
  TApproveApplicationUI,
  TChangeAnimalRestrictionState,
  TEditChargeDatesUI,
  TPredefinedConversionReasonUI,
  TDeleteAnimalUI,
  TAnimalHistoryItemDB,
  TAnimalHistoryItemUI,
} from 'data/types/Animal.types';
import {
  animalConverter,
  getAnimalConverter,
  vetRecordExpirationUpdateConverter,
  getAnimalWarningVerificationConverter,
  updateAnimalTypeConverter,
  approveAnimalTypeConversionConverter,
  rejectAnimalTypeConversionConverter,
  updatePetChargingStatusConverter,
  markAsPNCConverter,
  markKnownConverter,
  removeDiscoveredConverter,
  markAsDiscoveredAnimalConverter,
  updateAnimalNameConvertor,
  updateAnimalSpeciesConvertor,
  updateAnimalBreedConvertor,
  updateAnimalMicrochipConvertor,
  updateAnimalBirthDateConvertor,
  updateAnimalWeightConvertor,
  updateAnimalHouseTrainedConvertor,
  updateAnimalGenderConvertor,
  updateAnimalTrainedForConvertor,
  updateAnimalReproductiveConvertor,
  updateAnimalProfilePictureConvertor,
  updateAnimalColorsConvertor,
  updateAnimalAggressiveBehaviorConvertor,
  approveApplicationConverter,
  deleteAnimalConverter,
} from 'data/convertors/Animal.converters';
import { animalHistoryItemConverter } from 'data/convertors/AnimalHistory.converters';
import { authSplitApi } from '../helpers/slice.helpers';
import { EMethodDB, EMethodUI } from 'data/types/Applications.types';
import { methodsConvertor } from 'data/convertors/Application.converters';

export const animalApi = authSplitApi('animal', [
  'animal',
  'animalsList',
]).injectEndpoints({
  endpoints: build => ({
    verifyAnimal: build.mutation<void, { animalId: string }>({
      query({ animalId }) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.VERIFY}`,
          method: 'POST',
          body: { animalId },
        };
      },
      invalidatesTags: ['animal'],
    }),
    verifyAnimalWarning: build.query<
      TAnimalWarningVerificationUI,
      { animalId: string }
    >({
      query({ animalId }) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.VERIFY}/${EAnimalUrls.WARNING}`,
          method: 'GET',
          params: { animalId },
        };
      },
      providesTags: ['animal'],
      transformResponse: (data: TAnimalWarningVerificationDB) =>
        getAnimalWarningVerificationConverter.fromDb(data),
    }),
    retractAnimalVerification: build.mutation<void, { animalId: string }>({
      query({ animalId }) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.VERIFY}`,
          method: 'DELETE',
          body: { animalId },
        };
      },
      invalidatesTags: ['animal'],
    }),
    verifyAnimaVetRecord: build.mutation<
      void,
      { animalId: string; vetRecordId: string; vaccinationRecordId: string }
    >({
      query({ animalId, vetRecordId, vaccinationRecordId }) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.VET_RECORD}/${EAnimalUrls.VERIFY}`,
          method: 'POST',
          body: { animalId, vetRecordId, vaccinationRecordId },
        };
      },
      invalidatesTags: ['animal'],
    }),
    undoVerifyAnimaVetRecord: build.mutation<
      void,
      { animalId: string; vetRecordId: string; vaccinationRecordId: string }
    >({
      query({ animalId, vetRecordId, vaccinationRecordId }) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.VET_RECORD}/${EAnimalUrls.VERIFY}`,
          method: 'DELETE',
          body: { animalId, vetRecordId, vaccinationRecordId },
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalType: build.mutation<void, TUpdateAnimalTypeUI>({
      query(body) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.TYPE}`,
          method: 'PATCH',
          body: updateAnimalTypeConverter.toDb(body),
        };
      },
      invalidatesTags: ['animal', 'animals'],
    }),
    approveAnimalTypeConversion: build.mutation<void, TApproveAnimalTypeConvUI>(
      {
        query(body) {
          return {
            url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.CONVERSION}/${EAnimalUrls.APPROVE}`,
            method: 'PATCH',
            body: approveAnimalTypeConversionConverter.toDb(body),
          };
        },
        invalidatesTags: ['animal', 'animals'],
      },
    ),
    rejectAnimalTypeConversion: build.mutation<void, TRejectAnimalTypeConvUI>({
      query(body) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.CONVERSION}/${EAnimalUrls.REJECT}`,
          method: 'PATCH',
          body: rejectAnimalTypeConversionConverter.toDb(body),
        };
      },
      invalidatesTags: ['animal', 'animals'],
    }),
    updateVetRecordExpiration: build.mutation<
      void,
      TVetRecordExpirationUpdateUI
    >({
      query(body) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.VET_RECORDS}`,
          method: 'PUT',
          body: vetRecordExpirationUpdateConverter.toDb(body),
        };
      },
      invalidatesTags: ['animal', 'animals'],
    }),
    verifySADocPacket: build.mutation<
      void,
      { animalId: string; saDocumentPacketId: string }
    >({
      query({ animalId, saDocumentPacketId }) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.SA_DOC_PACKET}/${EAnimalUrls.VERIFY}`,
          method: 'POST',
          body: { animalId, saDocumentPacketId },
        };
      },
      invalidatesTags: ['animal'],
    }),
    undoVerifySADocPacket: build.mutation<
      void,
      { animalId: string; saDocumentPacketId: string }
    >({
      query({ animalId, saDocumentPacketId }) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.SA_DOC_PACKET}/${EAnimalUrls.VERIFY}`,
          method: 'DELETE',
          body: { animalId, saDocumentPacketId },
        };
      },
      invalidatesTags: ['animal'],
    }),
    getAnimal: build.query<TAnimalDetailUi, string>({
      query(id) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${id}`,
          method: 'GET',
        };
      },
      providesTags: ['animal'],
      transformResponse: (data: TAnimalDetail) =>
        getAnimalConverter.fromDb(data),
    }),
    /**
     * The changelog behind this is written asynchronously off the integration-event bus, so a row
     * for a change just made may not exist yet at refetch time. The tab offers a manual refresh
     * for that reason.
     */
    getAnimalHistory: build.query<TAnimalHistoryItemUI[], string>({
      query(animalId) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${animalId}/${EAnimalUrls.HISTORY}`,
          method: 'GET',
        };
      },
      providesTags: ['animal'],
      transformResponse: (data: TAnimalHistoryItemDB[]) =>
        (data || []).map(item => animalHistoryItemConverter.fromDb(item)),
    }),
    getAnimalsList: build.query<
      TPageableDataWithContentUi<TAnimalUI[]>,
      TTableFilters<{ [key: string]: string | string[] }, ESortFields>
    >({
      query({ page, pageSize, sortField, sortDirection, filters }) {
        const validFilters = animalConverter.toDb(filters);
        return {
          url: `${EAnimalUrls.ANIMAL}?page=${page}&size=${pageSize}`,
          method: 'GET',
          params: {
            ...(sortField && sortDirection && { sortField, sortDirection }),
            ...validFilters,
          },
        };
      },
      providesTags: ['animalsList'],
      transformResponse: (data: TPageableDataWithContentUi<TAnimalDB[]>) => {
        return {
          ...data,
          content: data.content.map(item => animalConverter.fromDb(item)),
        };
      },
    }),
    viewNewUpdates: build.mutation<void, string>({
      query(id) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${id}/${EAnimalUrls.VIEW}`,
          method: 'PATCH',
        };
      },
      invalidatesTags: ['animalsList'],
    }),
    updateChargingStatus: build.mutation<
      TChargingStatusUpdateDB,
      TChargingStatusUpdateUI
    >({
      query(body: TChargingStatusUpdateUI) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.CHARGING_STATUS}`,
          method: 'PATCH',
          body: updatePetChargingStatusConverter.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    markAsPNC: build.mutation<TMarkAsPNCDB, TMarkAsPNCUI>({
      query(body: TMarkAsPNCUI) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.TAGS}/${EAnimalUrls.PNC}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['animal'],
    }),
    markAsKnown: build.mutation<TMarkKnownDB, TMarkKnownUI>({
      query(body: TMarkKnownUI) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.TAGS}/${EAnimalUrls.KNOWN}`,
          method: 'POST',
          body: markKnownConverter.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    removePNC: build.mutation<TMarkAsPNCDB, TMarkAsPNCUI>({
      query(body: TMarkAsPNCUI) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.TAGS}/${EAnimalUrls.PNC}`,
          method: 'DELETE',
          body: markAsPNCConverter.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    removeKnown: build.mutation<TMarkKnownDB, TMarkKnownUI>({
      query(body: TMarkKnownUI) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.TAGS}/${EAnimalUrls.KNOWN}`,
          method: 'DELETE',
          body: markKnownConverter.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    removeDiscoverd: build.mutation<TRemoveDiscoveredDB, TRemoveDiscoveredUI>({
      query(body: TRemoveDiscoveredUI) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.TAGS}/${EAnimalUrls.DISCOVERED}`,
          method: 'DELETE',
          body: removeDiscoveredConverter.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    deleteAnimal: build.mutation<void, TDeleteAnimalUI>({
      query(body: TDeleteAnimalUI) {
        return {
          url: EAnimalUrls.ANIMAL,
          method: 'DELETE',
          body: deleteAnimalConverter.toDb(body),
        };
      },
      invalidatesTags: ['animal', 'animalsList'],
    }),
    markAsDiscoverd: build.mutation<TMarkAsDiscoverdDB, TMarkAsDiscoverdUI>({
      query(body: TMarkAsDiscoverdUI) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.TAGS}/${EAnimalUrls.DISCOVERED}`,
          method: 'POST',
          body: markAsDiscoveredAnimalConverter.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    attachVetRecord: build.mutation<void, TAnimalDetailVetRecordCreateUi>({
      query(body) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.ATTACH_VET_RECORD}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['animal'],
    }),
    // // Animal info Updates START
    updateAnimalName: build.mutation<void, TUpdateAnimalNameUI>({
      query(body) {
        return {
          url: `${EAnimalUrls.ANIMAL}/name`,
          method: 'PATCH',
          body: updateAnimalNameConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalSpecies: build.mutation<void, TUpdateAnimalSpeciesUI>({
      query(body) {
        return {
          url: `${EAnimalUrls.ANIMAL}/species`,
          method: 'PATCH',
          body: updateAnimalSpeciesConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalBreed: build.mutation<void, TUpdateAnimalBreedUI>({
      query(body) {
        return {
          url: `${EAnimalUrls.ANIMAL}/breed`,
          method: 'PATCH',
          body: updateAnimalBreedConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalMicrochipDate: build.mutation<
      void,
      TUpdateAnimalMicrochipDateUI
    >({
      query(body) {
        return {
          url: `${EAnimalUrls.ANIMAL}/microchip`,
          method: 'PATCH',
          body: updateAnimalMicrochipConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalBirthDate: build.mutation<void, TUpdateAnimalBirthDateUI>({
      query(body) {
        return {
          url: `${EAnimalUrls.ANIMAL}/birthdate`,
          method: 'PATCH',
          body: updateAnimalBirthDateConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalWeight: build.mutation<void, TUpdateAnimalWeightUI>({
      query(body) {
        return {
          url: `${EAnimalUrls.ANIMAL}/weight`,
          method: 'PATCH',
          body: updateAnimalWeightConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalHouseTrained: build.mutation<void, TUpdateAnimalHouseTrainedUI>(
      {
        query(body) {
          return {
            url: `${EAnimalUrls.ANIMAL}/house-trained`,
            method: 'PATCH',
            body: updateAnimalHouseTrainedConvertor.toDb(body),
          };
        },
        invalidatesTags: ['animal'],
      },
    ),
    updateAnimalGender: build.mutation<void, TUpdateAnimalGenderUI>({
      query(body) {
        return {
          url: `${EAnimalUrls.ANIMAL}/gender`,
          method: 'PATCH',
          body: updateAnimalGenderConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalTrainedFor: build.mutation<void, TUpdateAnimalTrainedForUI>({
      query(body) {
        return {
          url: `${EAnimalUrls.ANIMAL}/trained-for`,
          method: 'PATCH',
          body: updateAnimalTrainedForConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    // updateAnimalColors: build.mutation<void, TUpdateAnimalColorsUI>({
    //   query(body) {
    //     return {
    //       url: `${EAnimalUrls.ANIMAL}/colors`,
    //       method: 'PATCH',
    //       body: updateAnimalColorsConvertor.toDb(body),
    //     };
    //   },
    //   invalidatesTags: ['animal'],
    // }),

    updateAnimalReproductive: build.mutation<void, TUpdateAnimalReproductiveUI>(
      {
        query(body) {
          return {
            url: `${EAnimalUrls.ANIMAL}/reproductive`,
            method: 'PATCH',
            body: updateAnimalReproductiveConvertor.toDb(body),
          };
        },
        invalidatesTags: ['animal'],
      },
    ),

    updateAnimalProfilePicture: build.mutation<
      void,
      TUpdateAnimalProfilePictureUI
    >({
      query(body) {
        return {
          url: `${EAnimalUrls.ANIMAL}/photo`,
          method: 'PATCH',
          body: updateAnimalProfilePictureConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalColors: build.mutation<void, TUpdateAnimalColorsUI>({
      query(body) {
        return {
          url: `${EAnimalUrls.ANIMAL}/colors`,
          method: 'PATCH',
          body: updateAnimalColorsConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalAggressiveBehavior: build.mutation<
      void,
      TUpdateAnimalAggressiveBehaviorUI
    >({
      query(body) {
        return {
          url: `${EAnimalUrls.ANIMAL}/aggressive-behavior`,
          method: 'PATCH',
          body: updateAnimalAggressiveBehaviorConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    // Animal info Updates END
    // Animal Applications Actions START
    approveApplication: build.mutation<void, TApproveApplicationUI>({
      query(body) {
        return {
          url: `${EAnimalUrls.APPLICATION}/approve`,
          method: 'PATCH',
          body: approveApplicationConverter.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),

    retractAnimal: build.mutation<
      string,
      TAnimalApplicationTargetUI & { reason?: string }
    >({
      query(body) {
        return {
          url: `${EAnimalUrls.APPLICATION}/retract`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['animal'],
    }),
    abandonApplication: build.mutation<
      string,
      TAnimalApplicationTargetUI & { reason: string }
    >({
      query(body) {
        return {
          url: `${EAnimalUrls.APPLICATION}/abandon`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['animal'],
    }),
    dontApproveAnimalApplicant: build.mutation<
      void,
      TAnimalApplicationTargetUI & { rejectReason: string }
    >({
      query(body) {
        return {
          url: `${EAnimalUrls.APPLICATION}/reject`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['animal'],
    }),

    conditionalApplicantApprove: build.mutation<
      string,
      TAnimalApplicationTargetUI
    >({
      query(body) {
        return {
          url: `${EAnimalUrls.APPLICATION}/conditional-approved/approve`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['animal'],
    }),

    reApplyAnimal: build.mutation<
      string,
      TAnimalApplicationTargetUI & { reason?: string }
    >({
      query(body) {
        return {
          url: `${EAnimalUrls.APPLICATION}/reapply`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['animal'],
    }),
    getApplicationDetails: build.query<
      { method: EMethodUI },
      { applicationId: string }
    >({
      query({ applicationId }) {
        return {
          url: `${EAnimalUrls.APPLICATION}/move-in/details`,
          method: 'GET',
          params: { applicationId },
        };
      },
      providesTags: ['applicationDetails'],
      transformResponse: (data: { method: EMethodDB }) => {
        return {
          method: methodsConvertor.fromDb(data.method),
        };
      },
    }),

    // Which of the actions above this animal may actually be offered. Answered off the application
    // the animal would be given if it has none, so a known animal is not left with an empty header.
    getAllowedAnimalActions: build.query<
      TAllowedAnimalActionsUI,
      TAnimalApplicationTargetUI
    >({
      query: ({ tenantResidencyId, animalId }) => ({
        url: COMMON_ALLOWED_ANIMAL_ACTIONS_URL,
        method: 'GET',
        params: { tenantResidencyId, animalId },
      }),
      providesTags: ['animal'],
      transformResponse: (data: TAllowedAnimalActionsDB) =>
        allowedAnimalActionsConverter.fromDb(data),
    }),
    // Animal Applications Actions END
    changeAnimalRestrictionState: build.mutation<
      void,
      TChangeAnimalRestrictionState
    >({
      query(body: TChangeAnimalRestrictionState) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.RESTRICTION_STATE}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['animal'],
    }),

    editChargeDates: build.mutation<void, TEditChargeDatesUI>({
      query(body) {
        return {
          url: `${EAnimalUrls.ANIMAL}/charge-date`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['animal'],
    }),
    getPredefinedConversionReasons: build.query<
      TPredefinedConversionReasonUI[],
      void
    >({
      query() {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.CONVERSION}/${EAnimalUrls.PREDEFINED_REASONS}`,
          method: 'GET',
        };
      },
    }),
    removeConversion: build.mutation<
      void,
      { animalId: string; reason: string }
    >({
      query(body) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.CONVERSION}/${EAnimalUrls.REMOVE}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['animal', 'animalsList'],
    }),
    cancelConversionRemoved: build.mutation<void, { animalId: string }>({
      query(body) {
        return {
          url: `${EAnimalUrls.ANIMAL}/${EAnimalUrls.CONVERSION}/${EAnimalUrls.REMOVE}/${EAnimalUrls.CANCEL}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['animal', 'animalsList'],
    }),
  }),

  overrideExisting: false,
});

export const {
  useVerifyAnimalMutation,
  useRetractAnimalVerificationMutation,
  useVerifyAnimaVetRecordMutation,
  useVerifySADocPacketMutation,
  useVerifyAnimalWarningQuery,
  useUpdateAnimalTypeMutation,
  useApproveAnimalTypeConversionMutation,
  useRejectAnimalTypeConversionMutation,
  useUndoVerifyAnimaVetRecordMutation,
  useUpdateVetRecordExpirationMutation,
  useUndoVerifySADocPacketMutation,
  useGetAnimalsListQuery,
  useGetAnimalQuery,
  useGetAnimalHistoryQuery,
  useViewNewUpdatesMutation,
  useUpdateChargingStatusMutation,
  useMarkAsPNCMutation,
  useRemovePNCMutation,
  useMarkAsKnownMutation,
  useRemoveKnownMutation,
  useRemoveDiscoverdMutation,
  useMarkAsDiscoverdMutation,
  useAttachVetRecordMutation,
  useUpdateAnimalNameMutation,
  useUpdateAnimalSpeciesMutation,
  useUpdateAnimalBreedMutation,
  useUpdateAnimalMicrochipDateMutation,
  useUpdateAnimalBirthDateMutation,
  useUpdateAnimalGenderMutation,
  useUpdateAnimalWeightMutation,
  useUpdateAnimalHouseTrainedMutation,
  useUpdateAnimalTrainedForMutation,
  useUpdateAnimalReproductiveMutation,
  useUpdateAnimalProfilePictureMutation,
  useUpdateAnimalColorsMutation,
  useUpdateAnimalAggressiveBehaviorMutation,
  useApproveApplicationMutation,
  useRetractAnimalMutation,
  useAbandonApplicationMutation,
  useDontApproveAnimalApplicantMutation,
  useConditionalApplicantApproveMutation,
  useReApplyAnimalMutation,
  useGetAllowedAnimalActionsQuery,
  useLazyGetApplicationDetailsQuery,
  useChangeAnimalRestrictionStateMutation,
  useEditChargeDatesMutation,
  useGetPredefinedConversionReasonsQuery,
  useRemoveConversionMutation,
  useCancelConversionRemovedMutation,
  useDeleteAnimalMutation,
} = animalApi;
